import { cn } from '@/lib/utils';

type LogoWordmarkTone = 'light' | 'dark' | 'auto';

interface LogoProps {
  className?: string;
  title?: string;
}

interface LogoWordmarkProps extends LogoProps {
  tone?: LogoWordmarkTone;
}

interface BrandLogoProps {
  className?: string;
  markClassName?: string;
  wordmarkClassName?: string;
  tone?: LogoWordmarkTone;
}

const WORDMARK_TONE_CLASS: Record<LogoWordmarkTone, string> = {
  light: 'text-white',
  dark: 'text-navy-900',
  auto: 'qf-logo-wordmark',
};

export function Logo({ className = 'w-8 h-8', title = 'QuantForge logo mark' }: LogoProps) {
  return (
    <svg
      aria-label={title}
      className={cn('shrink-0', className)}
      role="img"
      viewBox="526 1721 455 523"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon fill="#4ED4CF" points="753.7 1721 753.7 1851.7 639.8 1917.1 528.3 1852.5" />
      <polygon fill="#28A397" points="753.7 1721 980.5 1851.7 867.1 1917.1 753.7 1851.7" />
      <polygon fill="#4ED4CF" points="866.8 1917.1 866.8 2047.8 980.9 1982.5 980.9 1851.7" />
      <polygon fill="#28A397" points="528.3 1852.5 526.1 2113.9 639.9 2047.1 639.8 1917.1" />
      <polygon fill="#4ED4CF" points="526.1 2113.9 639.9 2047.1 753.7 2113.9 753.6 2243.9" />
      <polygon fill="#28A397" points="866.8 2047.8 753.7 2113.9 753.6 2243.9 867.1 2178" />
      <polygon fill="#4ED4CF" points="980.5 2113.9 980.9 2243.9 867.1 2178 866.8 2047.8" />
    </svg>
  );
}

export function LogoWordmark({
  className = 'h-8 w-auto',
  tone = 'dark',
  title = 'QuantForge wordmark',
}: LogoWordmarkProps) {
  return (
    <svg
      aria-label={title}
      className={cn('w-auto', WORDMARK_TONE_CLASS[tone], className)}
      preserveAspectRatio="xMinYMid meet"
      role="img"
      viewBox="1072 1860 2525 420"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="currentColor"
        d="M1304.3 2218.8l-46.3-55.6c-11.2 2.8-23.6 4.2-37 4.2-27.8 0-53-6.5-75.5-19.4-22.6-12.9-40.4-31-53.4-54.3-13-23.3-19.6-49.5-19.6-78.7 0-29.2 6.5-55.3 19.6-78.5 13-23.1 30.9-41.2 53.4-54.1 22.6-12.9 47.8-19.4 75.5-19.4s53 6.5 75.5 19.4c22.6 12.9 40.4 30.9 53.4 54.1 13 23.1 19.6 49.3 19.6 78.5 0 27.2-5.6 51.8-16.8 73.9-11.2 22-26.8 39.8-46.7 53.2l70.3 76.6h-72.4zm-160.1-152.2c7.4 14.7 17.7 26 30.9 33.9 13.2 7.9 28.5 11.8 45.9 11.8 17.4 0 32.7-3.9 45.9-11.8 13.2-7.8 23.5-19.1 30.9-33.9 7.4-14.7 11.2-31.9 11.2-51.6 0-19.6-3.7-36.8-11.2-51.6-7.4-14.7-17.7-26-30.9-33.9-13.2-7.8-28.5-11.8-45.9-11.8-17.4 0-32.7 3.9-45.9 11.8-13.2 7.9-23.5 19.1-30.9 33.9-7.4 14.7-11.2 31.9-11.2 51.6 0 19.7 3.7 36.9 11.2 51.6z"
      />
      <path
        fill="currentColor"
        d="M1637.2 1931.3v233.1h-59.3v-47.1c-6.2 15.7-16.3 28-30.5 36.8-14.2 8.8-30.5 13.3-49 13.3-26.9 0-48.4-8.8-64.4-26.5-16-17.7-24-42.4-24-74.1v-135.5h58.9v128.8c0 18.5 4.8 32.9 14.3 43.1 9.5 10.2 22.4 15.4 38.7 15.4 17.1 0 30.7-5.5 40.8-16.4 10.1-10.9 15.2-26.6 15.2-47.1v-123.7h59.3z"
      />
      <path
        fill="currentColor"
        d="M1832.9 1942.3c14 9.3 24 21.7 29.9 37.5v-48.4h58.9v233.1h-58.9v-48.8c-5.9 15.7-15.9 28.3-29.9 37.7-14 9.4-30.9 14.1-50.5 14.1-19.6 0-37.3-4.8-53-14.3-15.7-9.5-28-23.4-36.8-41.4-8.8-18.1-13.3-39.3-13.3-63.8s4.4-45.7 13.3-63.8c8.8-18.1 21.1-31.9 36.8-41.4 15.7-9.5 33.4-14.3 53-14.3 19.6 0 36.5 4.6 50.5 13.9zm-76.6 55.9c-11.2 12.1-16.8 28.6-16.8 49.7 0 21 5.6 37.5 16.8 49.4 11.2 11.9 26.1 17.9 44.6 17.9 18 0 32.8-6.1 44.4-18.3 11.6-12.2 17.5-28.5 17.5-49 0-20.8-5.8-37.2-17.5-49.4-11.6-12.2-26.4-18.3-44.4-18.3-18.5 0-33.4 6-44.6 18z"
      />
      <path
        fill="currentColor"
        d="M2178.8 1954.5c16 17.7 24 42.5 24 74.5v135.5h-58.9v-128.8c0-18.5-4.8-32.9-14.3-43.1-9.5-10.2-22.6-15.4-39.1-15.4-16.8 0-30.3 5.5-40.4 16.4-10.1 10.9-15.2 26.7-15.2 47.1v123.7h-59.3v-233.1h59.3v46.7c6.2-15.7 16.3-28 30.5-36.8 14.2-8.8 30.5-13.3 49-13.3 26.9 0 48.4 8.8 64.4 26.5z"
      />
      <path
        fill="currentColor"
        d="M2379.5 2113.6v50.9h-31.1c-26.1 0-46.1-6.3-60-18.9-13.9-12.6-20.8-33.7-20.8-63.1v-101h-32.4v-50.1h32.4v-57.7h59.3v57.7h52.2v50.1h-52.2v101.8c0 11.2 2.2 19.1 6.7 23.6 4.5 4.5 12.1 6.7 22.7 6.7h23.2z"
      />
      <path
        fill="currentColor"
        d="M2606.3 1867.4v47.1h-125v79.5h100.2v45.4h-100.2v125H2422v-297.1h184.3z"
      />
      <path
        fill="currentColor"
        d="M2815.2 1942.7c18.1 9.5 32.3 23.4 42.7 41.4 10.4 18.1 15.6 39.3 15.6 63.8s-5.2 45.6-15.6 63.5c-10.4 18-24.6 31.8-42.7 41.4-18.1 9.7-38.5 14.5-61.2 14.5-22.7 0-43.1-4.8-61.2-14.5-18.1-9.7-32.3-23.5-42.7-41.4-10.4-18-15.6-39.1-15.6-63.5s5.2-45.7 15.6-63.8c10.4-18.1 24.6-31.9 42.7-41.4 18.1-9.5 38.5-14.3 61.2-14.3 22.7 0 43.1 4.8 61.2 14.3zm-103.3 54.5c-11.5 11.6-17.3 28.5-17.3 50.7 0 22.2 5.7 39.1 17.3 50.7 11.5 11.6 25.5 17.5 42.1 17.5 16.6 0 30.6-5.8 42.1-17.5 11.5-11.6 17.3-28.5 17.3-50.7 0-22.2-5.8-39.1-17.3-50.7-11.5-11.6-25.5-17.5-42.1-17.5-16.5 0-30.6 5.9-42.1 17.5z"
      />
      <path
        fill="currentColor"
        d="M3009.5 1940.8c14.3-8.6 30.3-12.8 48-12.8v63.5h-17.3c-43.5 0-65.2 20.2-65.2 60.6v112.4h-59.3v-233.1h59.3v44.6c8.6-14.9 20.1-26.6 34.5-35.2z"
      />
      <path
        fill="currentColor"
        d="M3232.1 1942.3c14 9.3 24 21.7 29.9 37.5v-48.4h58.9v234c0 21.6-4.3 41-12.8 58.3-8.6 17.3-21.3 30.8-38.3 40.6-17 9.8-37.5 14.7-61.7 14.7-33.7 0-60.9-8-81.6-24-20.8-16-33.4-37.7-37.9-65.2h58.5c3.4 11.8 9.9 21 19.6 27.6 9.7 6.6 22.1 9.9 37.2 9.9 17.4 0 31.4-5.1 42.1-15.4 10.7-10.2 16-25.7 16-46.5v-49.7c-5.9 15.7-15.9 28.3-29.9 37.7-14 9.4-30.9 14.1-50.5 14.1-19.6 0-37.3-4.8-53-14.3-15.7-9.5-28-23.4-36.8-41.4-8.8-18.1-13.3-39.3-13.3-63.8s4.4-45.7 13.3-63.8c8.8-18.1 21.1-31.9 36.8-41.4 15.7-9.5 33.4-14.3 53-14.3 19.6 0 36.5 4.6 50.5 13.9zm-76.6 55.9c-11.2 12.1-16.8 28.6-16.8 49.7 0 21 5.6 37.5 16.8 49.4 11.2 11.9 26.1 17.9 44.6 17.9 18 0 32.8-6.1 44.4-18.3 11.6-12.2 17.5-28.5 17.5-49 0-20.8-5.8-37.2-17.5-49.4-11.6-12.2-26.4-18.3-44.4-18.3-18.5 0-33.4 6-44.6 18z"
      />
      <path
        fill="currentColor"
        d="M3591.9 2059.3H3421c.8 20.8 6.5 36.1 16.8 45.9 10.4 9.8 23.3 14.7 38.7 14.7 13.7 0 25.2-3.4 34.3-10.1 9.1-6.7 14.9-15.9 17.5-27.4h62.7c-3.1 16.3-9.7 30.9-19.8 43.8-10.1 12.9-23.1 23-38.9 30.3-15.9 7.3-33.5 10.9-52.8 10.9-22.7 0-42.9-4.8-60.6-14.5-17.7-9.7-31.4-23.5-41.2-41.4-9.8-18-14.7-39.1-14.7-63.5s4.9-45.7 14.7-63.8c9.8-18.1 23.6-31.9 41.2-41.4 17.7-9.5 37.9-14.3 60.6-14.3 23 0 43.1 4.8 60.4 14.3 17.3 9.5 30.6 22.7 40 39.3 9.4 16.7 14.1 35.7 14.1 57 0 6.6-.7 13.3-2.1 20.9zm-72.6-69.5c-10.8-9.8-24.1-14.7-39.8-14.7-16 0-29.5 4.9-40.4 14.7-10.9 9.8-17 24.3-18.1 43.3h113.2c.8-19-4.1-33.4-14.9-43.3z"
      />
    </svg>
  );
}

export function BrandLogo({
  className,
  markClassName,
  wordmarkClassName,
  tone = 'light',
}: BrandLogoProps) {
  return (
    <div
      aria-label="QuantForge brand logo"
      className={cn('flex items-center gap-3', className)}
      role="img"
    >
      <Logo className={cn('h-10 w-10', markClassName)} />
      <LogoWordmark className={cn('h-8 w-auto', wordmarkClassName)} tone={tone} />
    </div>
  );
}
